import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin, Star, ArrowRight } from 'lucide-react';
import { destinations, luxuryHotels } from '../data/mock';
import { Button } from './ui/button';

export const DestinationPage = ({ destinationId, onNavigate }) => {
  const { t } = useTranslation();
  const destination = destinations.find(d => d.id === destinationId);

  if (!destination) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-stone-600">Destination not found</p>
      </div>
    );
  }

  const relatedHotels = luxuryHotels.filter(
    h => h.destination.toLowerCase().includes(destination.name.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px]">
        <img
          src={destination.heroImage || destination.image}
          alt={destination.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/30 to-stone-900/20" />
        
        {/* Back Button */}
        <div className="absolute top-24 left-4 sm:left-8 z-10">
          <Button
            variant="ghost"
            onClick={() => onNavigate('destinations')}
            className="text-white hover:bg-white/10 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Destinations
          </Button>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-16">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-amber-400 text-sm tracking-widest uppercase mb-3">
                {destination.tagline}
              </p>
              <h1 className="text-5xl sm:text-7xl font-serif text-white mb-4">
                {destination.name}
              </h1>
              <div className="flex flex-wrap items-center gap-6 text-white/80">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t('destinations.bestTime')}: {destination.bestTime}
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {destination.highlights?.length || 4} {t('destinations.highlights')}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h2 className="text-3xl font-serif text-stone-800 mb-6">Overview</h2>
                <p className="text-lg text-stone-600 leading-relaxed mb-8">
                  {destination.description}
                </p>

                {/* Highlights */}
                {destination.highlights && (
                  <div className="mb-12">
                    <h3 className="text-xl font-serif text-stone-800 mb-4">
                      {t('destinations.highlights')}
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {destination.highlights.map((highlight, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl"
                        >
                          <div className="w-2 h-2 bg-amber-500 rounded-full" />
                          <span className="text-stone-700">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sub-destinations */}
                {destination.subDestinations && (
                  <div>
                    <h3 className="text-xl font-serif text-stone-800 mb-4">
                      Explore {destination.name}
                    </h3>
                    <div className="space-y-4">
                      {destination.subDestinations.map((sub, index) => (
                        <div
                          key={index}
                          className="p-6 bg-gradient-to-r from-amber-50 to-transparent rounded-xl border border-amber-100"
                        >
                          <h4 className="font-medium text-stone-800 mb-2">
                            {sub.name}
                          </h4>
                          <p className="text-sm text-stone-600">
                            {sub.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="sticky top-24"
              >
                {/* CTA Card */}
                <div className="bg-stone-900 rounded-2xl p-8 text-white mb-8">
                  <h3 className="text-xl font-serif mb-4">
                    Plan Your {destination.name} Journey
                  </h3>
                  <p className="text-stone-400 text-sm mb-6">
                    Let our experts craft your perfect luxury itinerary
                  </p>
                  <Button
                    onClick={() => onNavigate('contact')}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    Request Itinerary
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>

                {/* Related Hotels */}
                {relatedHotels.length > 0 && (
                  <div>
                    <h3 className="text-lg font-serif text-stone-800 mb-4">
                      Luxury Hotels in {destination.name}
                    </h3>
                    <div className="space-y-4">
                      {relatedHotels.map((hotel) => (
                        <div
                          key={hotel.id}
                          className="bg-white rounded-xl shadow-lg shadow-stone-900/5 overflow-hidden"
                        >
                          <img
                            src={hotel.image}
                            alt={hotel.name}
                            className="w-full h-32 object-cover"
                          />
                          <div className="p-4">
                            <h4 className="font-medium text-stone-800 text-sm">
                              {hotel.name}
                            </h4>
                            <div className="flex items-center gap-1 mt-2">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Destinations */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-800 mb-8">Other Destinations</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {destinations
              .filter(d => d.id !== destinationId)
              .slice(0, 4)
              .map((dest) => (
                <motion.div
                  key={dest.id}
                  whileHover={{ y: -4 }}
                  onClick={() => onNavigate(`destination-${dest.id}`)}
                  className="cursor-pointer group"
                >
                  <div className="relative h-40 rounded-xl overflow-hidden">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <h3 className="text-white font-medium">{dest.name}</h3>
                      <p className="text-xs text-amber-300">{dest.tagline}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};
